import { CurrencyDto, CurrencyUpdateDto } from '@api/WalletClient';
import { FieldState, FormState } from 'formstate';
import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Checkbox } from '../components/checkbox';
import { ConfirmationModal } from '../components/confirmation-modal';
import { RadioGroup } from '../components/radio-group';
import { TextField } from '../components/textfield';
import { Api } from '../core/api';
import { notNegative, required, useModel } from '../core/forms';
import { injectTSDI } from '../core/tsdi';

type Mode = 'create' | 'edit';

type SymbolMode = 'text' | 'svg';

type Props = {
    mode: Mode;
    currency?: CurrencyDto | null;
    openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    onSuccess: () => void;
};

const SYMBOL_MODE_OPTIONS = [
    { label: 'Text / Emoji', value: 'text' as SymbolMode },
    { label: 'SVG Icon', value: 'svg' as SymbolMode }
];

const toNumber = (value: string | number) => {
    if (typeof value === 'number') {
        return value;
    }
    if (value === '') {
        return 0;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeOptional = (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
};

const SVG_MAX_LENGTH = 10_000;

function sanitizeSvg(raw: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');
    doc.querySelectorAll('script, foreignObject').forEach((el) => el.remove());
    doc.querySelectorAll('*').forEach((el) => {
        for (const attr of Array.from(el.attributes)) {
            if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
        }
    });
    return new XMLSerializer().serializeToString(doc.documentElement);
}

function isValidSvg(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return true;
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'image/svg+xml');
    const error = doc.querySelector('parsererror');
    if (error) return false;
    return doc.documentElement.tagName === 'svg';
}

const validateSvg = () => (value: string) => {
    if (!value.trim()) return false;
    if (value.length > SVG_MAX_LENGTH) {
        return `SVG must be under ${SVG_MAX_LENGTH.toLocaleString()} characters`;
    }
    if (!isValidSvg(value)) {
        return 'Invalid SVG markup';
    }
    return false;
};

const ToggleLabel = ({
    title,
    description
}: {
    title: string;
    description: string;
}) => (
    <span className="flex flex-col">
        <span>{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
    </span>
);

export const CurrencyModal = observer(
    ({ mode, currency, openState: [open, setOpen], onSuccess }: Props) => {
        const { walletClient } = injectTSDI(Api);
        const isEdit = mode === 'edit';

        const initialSymbolMode: SymbolMode = currency?.symbolSvg
            ? 'svg'
            : 'text';

        const [symbolMode, setSymbolMode] =
            React.useState<SymbolMode>(initialSymbolMode);
        const [pendingModeSwitch, setPendingModeSwitch] = React.useState(false);

        React.useEffect(() => {
            setSymbolMode(currency?.symbolSvg ? 'svg' : 'text');
        }, [currency?.code, open]);

        const model = useModel(
            () => {
                const form = new FormState({
                    name: new FieldState<string>(
                        currency?.name ?? ''
                    ).validators(required()),
                    code: new FieldState<string>(
                        currency?.code ?? ''
                    ).validators(required()),
                    symbol: new FieldState<string>(
                        currency?.symbol ?? ''
                    ).validators(required()),
                    symbolSvg: new FieldState<string>(
                        currency?.symbolSvg ?? ''
                    ).validators(validateSvg()),
                    precision: new FieldState<number>(
                        currency?.precision ?? 2
                    ).validators(notNegative()),
                    exchangeRateEnabled: new FieldState<boolean>(
                        currency?.exchangeRateEnabled ?? false
                    ),
                    shopEnabled: new FieldState<boolean>(
                        currency?.shopEnabled ?? false
                    ),
                    walletEnabled: new FieldState<boolean>(
                        currency?.walletEnabled ?? false
                    ),
                    hideOnDashboard: new FieldState<boolean>(
                        currency?.hideOnDashboard ?? false
                    )
                });

                return {
                    form,
                    fields: {
                        name: form.$.name,
                        code: form.$.code,
                        symbol: form.$.symbol,
                        symbolSvg: form.$.symbolSvg,
                        precision: form.$.precision,
                        exchangeRateEnabled: form.$.exchangeRateEnabled,
                        shopEnabled: form.$.shopEnabled,
                        walletEnabled: form.$.walletEnabled,
                        hideOnDashboard: form.$.hideOnDashboard
                    }
                };
            },
            undefined,
            [currency?.code, open, mode]
        );

        const handleSubmit = async () => {
            // Adjust validators based on symbol mode:
            // - SVG mode: symbol text is hidden, SVG is required
            // - Text mode: symbol text is required, SVG validation is skipped
            if (symbolMode === 'svg') {
                model.fields.symbol.validators(() => false);
                model.fields.symbolSvg.validators(required(), validateSvg());
            } else {
                model.fields.symbol.validators(required());
                model.fields.symbolSvg.validators(validateSvg());
            }

            const validation = await model.form.validate();
            if (validation.hasError) {
                return false;
            }

            const rawSvg =
                symbolMode === 'svg'
                    ? normalizeOptional(model.fields.symbolSvg.value)
                    : undefined;
            const safeSvg = rawSvg ? sanitizeSvg(rawSvg) : undefined;
            const symbol =
                symbolMode === 'svg'
                    ? undefined
                    : model.fields.symbol.value || undefined;
            const requestBase: CurrencyUpdateDto = {
                name: model.fields.name.value,
                symbol,
                symbolSvg: safeSvg ? btoa(safeSvg) : undefined,
                exchangeRateEnabled: model.fields.exchangeRateEnabled.value,
                shopEnabled: model.fields.shopEnabled.value,
                walletEnabled: model.fields.walletEnabled.value,
                hideOnDashboard: model.fields.hideOnDashboard.value
            };

            if (isEdit) {
                const code = currency?.code || model.fields.code.value;
                await walletClient.supportApi.currencySupportControllerUpdateCurrency(
                    code,
                    requestBase
                );
            } else {
                const request: CurrencyDto = {
                    ...requestBase,
                    code: model.fields.code.value,
                    precision: model.fields.precision.value
                };

                await walletClient.supportApi.currencySupportControllerCreateCurrency(
                    request
                );
            }

            onSuccess();
            return true;
        };

        return (
            <ConfirmationModal
                title={isEdit ? 'Edit Currency' : 'Create Currency'}
                buttonText={isEdit ? 'Save' : 'Create'}
                open={open}
                onModalClose={() => void setOpen(false)}
                submitHandler={() => handleSubmit()}
            >
                <TextField
                    label="Name"
                    required
                    type="text"
                    error={model.fields.name.error}
                    helperText={Boolean(model.fields.name.error)}
                    value={model.fields.name.value}
                    onChange={(value: string) => {
                        model.fields.name.onChange(value);
                    }}
                />
                <TextField
                    label="Code"
                    required
                    type="text"
                    disabled={isEdit}
                    error={model.fields.code.error}
                    helperText={Boolean(model.fields.code.error)}
                    value={model.fields.code.value}
                    onChange={(value: string) => {
                        model.fields.code.onChange(value.toUpperCase());
                    }}
                />
                {isEdit && (
                    <span className="text-xs text-muted-foreground">
                        Code and precision can only be set during creation.
                    </span>
                )}

                <RadioGroup<SymbolMode>
                    label="Symbol type"
                    value={symbolMode}
                    options={SYMBOL_MODE_OPTIONS}
                    onChange={(newMode) => {
                        if (
                            newMode === 'text' &&
                            model.fields.symbolSvg.value.trim()
                        ) {
                            setPendingModeSwitch(true);
                        } else {
                            setSymbolMode(newMode);
                        }
                    }}
                />

                {symbolMode === 'text' && (
                    <div className="flex flex-col gap-1">
                        <TextField
                            label="Symbol"
                            required
                            type="text"
                            error={model.fields.symbol.error}
                            helperText={Boolean(model.fields.symbol.error)}
                            value={model.fields.symbol.value}
                            onChange={(value: string) => {
                                model.fields.symbol.onChange(value);
                            }}
                        />
                        <span className="text-xs text-muted-foreground">
                            {'e.g. €, $, 🪙, ⭐'}
                        </span>
                    </div>
                )}

                {symbolMode === 'svg' && (
                    <div className="flex items-start gap-4">
                        <div className="flex flex-1 flex-col gap-1">
                            <TextField
                                label="Symbol SVG"
                                type="text"
                                error={model.fields.symbolSvg.error}
                                helperText={Boolean(
                                    model.fields.symbolSvg.error
                                )}
                                value={model.fields.symbolSvg.value}
                                onChange={(value: string) => {
                                    model.fields.symbolSvg.onChange(value);
                                }}
                            />
                            <span className="text-xs text-muted-foreground">
                                SVG icon takes priority over text symbol in the
                                mobile app
                            </span>
                        </div>
                        {model.fields.symbolSvg.value.trim() &&
                            isValidSvg(model.fields.symbolSvg.value) && (
                                <div
                                    className="mt-6 flex size-12 items-center justify-center rounded-md border [&_svg]:max-h-10 [&_svg]:max-w-10"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeSvg(
                                            model.fields.symbolSvg.value
                                        )
                                    }}
                                />
                            )}
                    </div>
                )}

                <TextField
                    label="Precision"
                    required
                    type="number"
                    disabled={isEdit}
                    error={model.fields.precision.error}
                    helperText={Boolean(model.fields.precision.error)}
                    value={model.fields.precision.value}
                    onChange={(value: string | number) => {
                        model.fields.precision.onChange(toNumber(value));
                    }}
                />
                {isEdit && (
                    <span className="text-xs text-muted-foreground">
                        Code and precision can only be set during creation.
                    </span>
                )}

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-muted-foreground">
                        Availability &amp; Visibility
                    </span>
                    <span className="text-xs text-muted-foreground">
                        These toggles affect mobile app visibility and
                        navigation; they do not block backend operations or
                        change balances.
                    </span>
                    <Checkbox
                        checked={model.fields.exchangeRateEnabled.value}
                        onChange={(event) => {
                            model.fields.exchangeRateEnabled.onChange(
                                event.target.checked
                            );
                        }}
                    >
                        <ToggleLabel
                            title="Show exchange rate in wallet"
                            description="Shows converted value next to balances in wallet and wallet dashboard cards for this currency."
                        />
                    </Checkbox>
                    <Checkbox
                        checked={model.fields.shopEnabled.value}
                        onChange={(event) => {
                            model.fields.shopEnabled.onChange(
                                event.target.checked
                            );
                        }}
                    >
                        <ToggleLabel
                            title="Enable rewards shop"
                            description="Makes this currency available in rewards shop currency filters and vendor requests."
                        />
                    </Checkbox>
                    <Checkbox
                        checked={model.fields.walletEnabled.value}
                        onChange={(event) => {
                            model.fields.walletEnabled.onChange(
                                event.target.checked
                            );
                        }}
                    >
                        <ToggleLabel
                            title="Enable wallet page access"
                            description="Allows selecting this currency in wallet filters (for multi-account users) and opening wallet from dashboard cards."
                        />
                    </Checkbox>
                    <Checkbox
                        checked={!model.fields.hideOnDashboard.value}
                        onChange={(event) => {
                            model.fields.hideOnDashboard.onChange(
                                !event.target.checked
                            );
                        }}
                    >
                        <ToggleLabel
                            title="Show on dashboard"
                            description="Controls visibility of this non-star currency card in the wallet dashboard section only."
                        />
                    </Checkbox>
                </div>

                <Dialog
                    open={pendingModeSwitch}
                    onOpenChange={(next) =>
                        !next && setPendingModeSwitch(false)
                    }
                >
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Switch to Text symbol?</DialogTitle>
                            <DialogDescription>
                                The SVG icon data will be cleared. This cannot
                                be undone after saving.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="ghost"
                                onClick={() => setPendingModeSwitch(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    model.fields.symbolSvg.onChange('');
                                    setSymbolMode('text');
                                    setPendingModeSwitch(false);
                                }}
                            >
                                Clear SVG
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </ConfirmationModal>
        );
    }
);
